import { useRef, useState } from 'react';
import { AlertTriangle, Check } from 'lucide-react';
import type { AdminClient } from '@baex/shared';
import { Button } from '../../components/ui/Button';
import { ErrorNotice } from '../../components/ui/ErrorNotice';
import { TextField } from '../../components/ui/Field';
import { Modal } from '../../components/ui/Modal';
import { formatBytes } from '../../lib/format';

/**
 * The type-to-confirm gate stays exactly as strict as it was — nothing is deleted until
 * the typed text matches the Client ID character for character. What changes is that the
 * gate stops being invisible: the button is pressable, and pressing it early says this
 * and puts the cursor in the box (see the "Admin Interface" and "Delete a client" sections).
 */
export const CONFIRM_TEXT_MISMATCH = 'Type the Client ID exactly as shown to confirm.';

/** Type-to-confirm deletion (see the "Delete a client" section) — the most destructive action in the console. */
export function DeleteClientModal({
  client,
  onCancel,
  onConfirm,
}: {
  client: AdminClient | null;
  onCancel: () => void;
  onConfirm: (client: AdminClient) => Promise<void>;
}) {
  const [typed, setTyped] = useState('');
  const [typedError, setTypedError] = useState<string | undefined>(undefined);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const typedRef = useRef<HTMLInputElement | null>(null);

  if (!client) return null;

  // Case-sensitive exact match, per the spec.
  const matches = typed === client.clientId;

  function close() {
    setTyped('');
    setTypedError(undefined);
    setError(null);
    setBusy(false);
    onCancel();
  }

  async function confirm() {
    if (!client) return;
    if (!matches) {
      // Nothing is deleted, and now the reader is told why rather than left pressing a
      // dead control.
      setTypedError(CONFIRM_TEXT_MISMATCH);
      typedRef.current?.focus();
      return;
    }
    setTypedError(undefined);
    setBusy(true);
    setError(null);
    try {
      await onConfirm(client);
      setTyped('');
      setBusy(false);
    } catch (caught) {
      setError(caught);
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      title={`Delete ${client.clientId}`}
      onClose={close}
      footer={
        <>
          <Button onClick={close}>Cancel</Button>
          {/* Disabled only while the deletion it started is running (see the "Admin Interface" section). */}
          <Button variant="danger" disabled={busy} onClick={() => void confirm()}>
            {busy ? 'Deleting…' : 'Delete'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {client.filesWaiting > 0 ? (
          <div className="flex items-start gap-2 rounded border border-danger bg-danger-soft px-3 py-2">
            <AlertTriangle size={15} className="mt-0.5 shrink-0 text-danger" aria-hidden="true" />
            <p className="text-[13px] text-ink">
              <span className="tabular">{client.clientId}</span> still has {client.filesWaiting}{' '}
              undownloaded {client.filesWaiting === 1 ? 'file' : 'files'} totaling{' '}
              {formatBytes(client.bytesWaiting)}. Deleting this client will permanently delete its
              entire folder and every file in it, including these. This cannot be undone.
            </p>
          </div>
        ) : null}

        <div className="relative">
          <TextField
            ref={typedRef}
            label={`Type ${client.clientId} to confirm deletion.`}
            value={typed}
            error={typedError}
            autoComplete="off"
            spellCheck={false}
            onChange={(event) => {
              setTyped(event.target.value);
              // The complaint clears as soon as the reader starts fixing it.
              if (typedError) setTypedError(undefined);
            }}
          />
          {matches ? (
            <span
              className="absolute right-2 top-7 text-success"
              role="status"
              aria-label="Client ID matches"
            >
              <Check size={15} aria-hidden="true" />
            </span>
          ) : null}
        </div>

        {error ? <ErrorNotice error={error} fallback="The client could not be deleted." /> : null}
      </div>
    </Modal>
  );
}
